import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import CourseForm from './CourseForm';
import CourseDetail from './CourseDetail';
import CourseContentManager from './CourseContentManager';
import './CourseManagement.css';

const CourseManagement = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCourse, setEditingCourse] = useState(null);
    const [viewingCourse, setViewingCourse] = useState(null);
    const [managingContent, setManagingContent] = useState(null);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/courses`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });
            const data = await response.json();
            setCourses(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching courses:', error);
            setLoading(false);
        }
    };

    const handleCreateCourse = () => {
        setEditingCourse(null);
        setShowForm(true);
    };

    const handleEditCourse = (course) => {
        setEditingCourse(course);
        setShowForm(true);
    };

    const handleDeleteCourse = async (courseId) => {
        if (window.confirm('Are you sure you want to delete this course?')) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/courses/${courseId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    credentials: 'include'
                });
                
                if (response.ok) {
                    setMessage('Course deleted successfully!');
                    fetchCourses();
                    setTimeout(() => setMessage(''), 3000);
                } else {
                    setMessage('Failed to delete course');
                    setTimeout(() => setMessage(''), 3000);
                }
            } catch (error) {
                setMessage('Error deleting course');
                setTimeout(() => setMessage(''), 3000);
            }
        }
    };

    const handleSaveCourse = (savedCourse) => {
        setMessage(editingCourse ? 'Course updated successfully!' : 'Course created successfully!');
        setShowForm(false);
        setEditingCourse(null);
        fetchCourses();
        setTimeout(() => setMessage(''), 3000);
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingCourse(null);
    };

    const handleViewCourse = (course) => {
        setViewingCourse(course);
    };

    const handleBackFromDetail = () => {
        setViewingCourse(null);
    };

    const handleManageContent = (course) => {
        setManagingContent(course);
    };

    const handleBackFromContentManager = () => {
        setManagingContent(null);
    };

    if (showForm) {
        return (
            <CourseForm
                course={editingCourse}
                onSave={handleSaveCourse}
                onCancel={handleCancelForm}
            />
        );
    }

    if (viewingCourse) {
        return (
            <CourseDetail
                course={viewingCourse}
                onBack={handleBackFromDetail}
            />
        );
    }

    if (managingContent) {
        return (
            <CourseContentManager
                course={managingContent}
                onBack={handleBackFromContentManager}
            />
        );
    }

    if (loading) {
        return <div className="course-management">Loading...</div>;
    }

    return (
        <div className="course-management">
            <div className="course-management-header">
                <h2>Course Management</h2>
                <button className="create-course-btn" onClick={handleCreateCourse}>
                    + Create New Course
                </button>
            </div>

            {message && (
                <div className="message">
                    {message}
                </div>
            )}

            <div className="courses-grid">
                {courses.length === 0 ? (
                    <div className="no-courses">
                        <p>No courses available. Create your first course!</p>
                    </div>
                ) : (
                    courses.map(course => (
                        <div key={course.id} className="course-card">
                            <div className="course-image">
                                {course.imageUrl ? (
                                    <img src={course.imageUrl} alt={course.title} />
                                ) : (
                                    <div className="course-placeholder">
                                        <span>📚</span>
                                    </div>
                                )}
                                {/* Course Type Badge */}
                                <div className="course-type-badge" style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    background: course.courseType === 'LIVE' ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                                }}>
                                    {course.courseType === 'LIVE' ? '🔴 LIVE' : '🎬 RECORDED'}
                                </div>
                            </div>
                            
                            <div className="course-content">
                                <h3>{course.title}</h3>
                                <p className="course-instructor">By {course.instructor}</p>
                                <p className="course-description">{course.description}</p>
                                
                                <div className="course-details">
                                    <span className="course-category">{course.category}</span>
                                    <span className="course-price">₹{course.price}</span>
                                    {course.durationHours && (
                                        <span className="course-duration">{course.durationHours}h</span>
                                    )}
                                    {course.courseType === 'LIVE' && course.maxStudents && (
                                        <span className="course-seats" style={{background: '#feebc8', color: '#7c2d12', padding: '4px 8px', borderRadius: '4px', fontSize: '12px'}}>
                                            👥 {course.currentEnrollments || 0}/{course.maxStudents} seats
                                        </span>
                                    )}
                                </div>
                                
                                <div className="course-actions">
                                    <button 
                                        className="view-btn"
                                        onClick={() => handleViewCourse(course)}
                                    >
                                        View Course
                                    </button>
                                    <button 
                                        className="edit-btn"
                                        onClick={() => handleEditCourse(course)}
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        className="manage-content-btn"
                                        onClick={() => handleManageContent(course)}
                                    >
                                        📝 Manage Content
                                    </button>
                                    <button 
                                        className="delete-btn"
                                        onClick={() => handleDeleteCourse(course.id)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default CourseManagement; 
